/**
 * /sign-up, Olytic workspace signup.
 *
 * Renders inside Foundation's AuthLayout. Fields: name + email + optional
 * company name + password + confirm password. Inline validation on blur for
 * password length + match. Submit POSTs to /api/signup which creates the Neon Auth user AND
 * runs the 4-row tenant bootstrap atomically.
 */

import { AuthLayout, Button, Checkbox, PasswordInput } from '@olytic/foundation';
import { PASSWORD_MIN_LENGTH, PASSWORD_POLICY_HINT, validatePassword } from '@olytic/shared';
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { authFrame } from '../components/authFrame.tsx';
import { pickAuthProgressCopy, useAuthElapsedProgress } from '../hooks/useAuthElapsedProgress.ts';
import { useSession } from '../hooks/useSession.tsx';
import { ApiError, signUp } from '../lib/api.ts';

export function SignUp() {
  const { status, refresh, hydrate } = useSession();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  // SIGNUP-FIX (2026-08-10): when the server returns 409 email_taken,
  // render an actionable prompt with Sign in + Reset password links
  // (email prefill) instead of a dead-end toast. State flips to true
  // so the UI knows to render the two-link callout.
  const [emailExists, setEmailExists] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmBlurred, setConfirmBlurred] = useState(false);
  const [passwordBlurred, setPasswordBlurred] = useState(false);
  // Push #82.6 item E — Privacy/Terms checkbox. Required before signup
  // proceeds; legal opt-in for every new owner.
  const [acceptTerms, setAcceptTerms] = useState(false);
  // Sub-agent NO-ABORT (2026-08-13) — timed-out recovery UI. When
  // signUp() throws with code === 'signup_timeout' (90s client belt
  // fired), we render a recovery callout with retry + "sign in with
  // your email" links rather than a bare toast. The email the user
  // just entered is preserved on the callout so a retry doesn't
  // demand a re-type. Compensating delete + D-185 dedupe on the
  // server side make a retry safe: either it succeeds fresh or
  // returns 409 email_taken (already handled).
  const [timedOutEmail, setTimedOutEmail] = useState<string | null>(null);

  // Sub-agent NO-ABORT (2026-08-13) — elapsed-time driver for the
  // progressive copy transitions on the loading card. Ticks while
  // `submitting` is true; resets to 0 when the submit finishes.
  const elapsedMs = useAuthElapsedProgress(submitting);

  // Sub-agent NO-ABORT (2026-08-13) — Neon warmup ping. Fire-and-forget
  // on mount so cold Neon compute is already warm by the time the user
  // hits Submit. No response handling — even the network handoff is
  // enough to kick the compute up while the form is being filled in.
  // Guarded against firing when the user is already authenticated
  // (they'd be redirected out immediately anyway).
  useEffect(() => {
    if (status === 'authenticated') return;
    try {
      void fetch('/api/health/warm', {
        method: 'GET',
        credentials: 'omit',
        // keepalive lets the request survive a nav-away shortly after
        // (though we don't actually depend on it — best-effort).
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      /* noop — best-effort warmup */
    }
  }, [status]);

  if (status === 'authenticated') {
    return <Navigate to="/home" replace />;
  }

  // Inline validation triggers on blur (UX-DESIGN.md § interactions:
  // "inline validation on blur, not on every keystroke").
  // Sub-agent PRE-LAUNCH-FIX (2026-08-02, FIX 4) — policy uplifted to
  // shared validatePassword() (min 12 + 3-of-4 categories + common-
  // password reject). `passwordPolicyMsg` is null when the pw passes
  // the full policy; hint text otherwise.
  const passwordMismatch = confirmBlurred && confirmPassword !== '' && password !== confirmPassword;
  const passwordPolicyResult =
    passwordBlurred && password !== '' ? validatePassword(password) : null;
  const passwordPolicyMsg =
    passwordPolicyResult !== null && !passwordPolicyResult.ok
      ? (passwordPolicyResult.error ?? null)
      : null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEmailExists(false);
    setTimedOutEmail(null);

    if (name.trim() === '' || email.trim() === '' || password === '') {
      setError('Name, email, and password are required.');
      return;
    }
    const pwCheck = validatePassword(password);
    if (!pwCheck.ok) {
      setError(pwCheck.error ?? 'Password does not meet the policy.');
      setPasswordBlurred(true);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      setConfirmBlurred(true);
      return;
    }
    if (!acceptTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await signUp(
        email.trim(),
        password,
        name.trim(),
        companyName.trim() || undefined,
      );
      // Push #88 (401 race fix, Option B): hydrate session state directly
      // from the response body so /home mounts already-authenticated. No
      // /api/me on first paint = no race with Set-Cookie persistence.
      if (res.user !== undefined) {
        hydrate(res.user);
      } else {
        refresh();
      }
      navigate('/home');
    } catch (err) {
      if (err instanceof ApiError) {
        // D-185 — one user_id = one active account. Server-side surfaces
        // friendly copy via err.message when err.code === 'd185_already_member';
        // pass through verbatim (the helper produces the right message).
        if (err.code === 'd185_already_member') {
          setError(err.message);
        } else if (err.status === 409 || err.code === 'email_taken') {
          // SIGNUP-FIX (2026-08-10): actionable state — the render
          // path swaps the plain toast for a two-link callout
          // (Sign in / Reset password, both with email prefill).
          setEmailExists(true);
        } else if (err.code === 'signup_timeout') {
          // Sub-agent NO-ABORT (2026-08-13) — 90s client belt fired.
          // Server may have finished bootstrapping past this point;
          // the user's account may already exist. Recovery UI offers
          // retry + "sign in with the email you entered" (in case the
          // bootstrap finished before the client aborted).
          setTimedOutEmail(email.trim());
        } else {
          setError(err.message);
        }
      } else {
        setError('Sign up failed. Please try again.');
      }
      setSubmitting(false);
    }
  }

  // Mirror SignIn: swap the whole form for a loading card during submission
  // so the populated inputs never look briefly empty during refresh()/navigate().
  //
  // Sub-agent NO-ABORT (2026-08-13): loading card copy now shifts on
  // elapsed-time thresholds via pickAuthProgressCopy so a slow signup
  // reads as "still working" instead of "stuck". 90s belt in signUp()
  // catches genuine hard hangs and routes to the recovery UI below.
  if (submitting) {
    const copy = pickAuthProgressCopy('signup', elapsedMs);
    return (
      <AuthLayout {...authFrame} title={copy.title} description="One moment.">
        <div className="flex flex-col items-center justify-center py-6">
          <span className="spinner spinner-lg" />
          <p className="muted" style={{ margin: '14px 0 0', fontSize: '13px' }}>
            {copy.subtitle}
          </p>
        </div>
      </AuthLayout>
    );
  }

  // Sub-agent NO-ABORT (2026-08-13) — recovery UI when the 90s client
  // belt fired. Server may still be finishing the bootstrap; from the
  // user's perspective the safest paths are (a) sign in with the email
  // they entered (if the bootstrap actually completed), or (b) retry
  // the signup (D-185 dedupe + compensating delete make this safe).
  if (timedOutEmail !== null) {
    return (
      <AuthLayout
        {...authFrame}
        title="Still working on it"
        description="Your signup is taking longer than usual."
      >
        <div className="callout callout-info" style={{ marginBottom: 16 }}>
          <p className="callout-body">
            Your account may already be set up. Please don't refresh — instead:
          </p>
          <p className="callout-body" style={{ marginTop: 10 }}>
            <Link
              to={`/sign-in?email=${encodeURIComponent(timedOutEmail)}`}
              style={{ color: 'var(--olytic-teal)', fontWeight: 500 }}
            >
              Sign in with {timedOutEmail}
            </Link>
          </p>
          <p className="callout-body" style={{ marginTop: 6, fontSize: 12 }}>
            If sign-in doesn't work, come back here and try again — you'll either get the "already
            exists" message (proof your account is set up) or a fresh signup.
          </p>
        </div>
        <Button
          type="button"
          variant="brand"
          style={{ width: '100%' }}
          onClick={() => setTimedOutEmail(null)}
        >
          Try signup again
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      {...authFrame}
      title="Create your account"
      description="Spin up an Olytic workspace in seconds."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/sign-in" style={{ color: 'var(--olytic-obsidian)', fontWeight: 500 }}>
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-row">
          <label className="form-label" htmlFor="name">
            Your name
          </label>
          <input
            id="name"
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith"
            autoComplete="name"
            required
            disabled={submitting}
            // Round 6 Item 19 — focus first input on mount.
            autoFocus
          />
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="email">
            Work email
          </label>
          <input
            id="email"
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            required
            disabled={submitting}
          />
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="company">
            Workspace name{' '}
            <span style={{ color: 'var(--fg-muted)', fontWeight: 400 }}>(optional)</span>
          </label>
          <input
            id="company"
            type="text"
            className="input"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Acme Co"
            autoComplete="organization"
            disabled={submitting}
          />
          <div className="form-hint">Leave blank to default to "{name.trim() || 'Your name'}".</div>
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="password">
            Password
          </label>
          <div onBlur={() => setPasswordBlurred(true)}>
            <PasswordInput
              id="password"
              placeholder=""
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              required
              disabled={submitting}
            />
          </div>
          <div
            className="form-hint"
            style={{ color: passwordPolicyMsg !== null ? 'var(--olytic-red)' : undefined }}
          >
            {passwordPolicyMsg ?? PASSWORD_POLICY_HINT}
          </div>
        </div>

        <ConfirmPasswordRow
          value={confirmPassword}
          onChange={setConfirmPassword}
          onBlur={() => setConfirmBlurred(true)}
          mismatch={passwordMismatch}
          disabled={submitting}
        />

        {/* Push #82.6 item E. Privacy/Terms opt-in. Required for owner
            signup.
            Sub-agent WWWWW (2026-07-31) — links point to the in-app
            /legal pages (public per Push 4-1g, reachable pre-auth) so a
            new signup can read the actual policy content without leaving
            the SPA. Legal.tsx auto-adapts to signed-out mode with a
            minimal branded chrome; /legal?tab=terms + ?tab=privacy tab
            keys already exist. */}
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            fontSize: 12,
            lineHeight: 1.55,
            margin: '4px 0',
            cursor: 'pointer',
          }}
        >
          <span style={{ flexShrink: 0, marginTop: 2 }}>
            <Checkbox
              checked={acceptTerms}
              onCheckedChange={(checked) => setAcceptTerms(checked === true)}
              disabled={submitting}
              aria-label="I agree to Olytic's Terms of Service and Privacy Policy"
            />
          </span>
          <span className="muted">
            I agree to Olytic's{' '}
            <Link
              to="/legal?tab=terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--olytic-teal)' }}
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              to="/legal?tab=privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--olytic-teal)' }}
            >
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        {emailExists && (
          <div className="callout callout-danger">
            <p className="callout-body">
              An account with <strong>{email.trim()}</strong> already exists.
            </p>
            <p className="callout-body" style={{ marginTop: 6 }}>
              <Link
                to={`/sign-in?email=${encodeURIComponent(email.trim())}`}
                style={{ color: 'var(--olytic-teal)', fontWeight: 500 }}
              >
                Sign in instead
              </Link>
              {' · '}
              <Link
                to={`/forgot-password?email=${encodeURIComponent(email.trim())}`}
                style={{ color: 'var(--olytic-teal)', fontWeight: 500 }}
              >
                Reset password
              </Link>
            </p>
          </div>
        )}

        {error !== null && !emailExists && (
          <div className="callout callout-danger">
            <p className="callout-body">{error}</p>
          </div>
        )}

        <Button
          type="submit"
          variant="brand"
          style={{ width: '100%' }}
          disabled={submitting || passwordMismatch || passwordPolicyMsg !== null || !acceptTerms}
        >
          {submitting ? (
            <span className="loading-inline">
              <span className="spinner" /> Creating account…
            </span>
          ) : (
            'Create account'
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}

const CONFIRM_MISMATCH_ID = 'confirmPassword-mismatch';

/** The confirm field. A mismatch message is the field's accessible description. */
export function ConfirmPasswordRow({
  value,
  onChange,
  onBlur,
  mismatch,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  mismatch: boolean;
  disabled: boolean;
}) {
  return (
    <div className="form-row">
      <label className="form-label" htmlFor="confirmPassword">
        Confirm password
      </label>
      <div onBlur={onBlur}>
        <PasswordInput
          id="confirmPassword"
          placeholder=""
          value={value}
          onChange={onChange}
          autoComplete="new-password"
          required
          disabled={disabled}
          aria-describedby={mismatch ? CONFIRM_MISMATCH_ID : undefined}
          aria-invalid={mismatch || undefined}
        />
      </div>
      {mismatch && (
        <div id={CONFIRM_MISMATCH_ID} className="form-hint" style={{ color: 'var(--olytic-red)' }}>
          Passwords don't match.
        </div>
      )}
    </div>
  );
}
